import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Loader2, RefreshCw, Wrench } from "lucide-react";
import { getTicketBuckets } from "./ticketApi";
import { supportTicketPath } from "./supportUrls";

const BUCKET_ICONS = {
  new: Inbox,
  engineering: Wrench,
};

const PRIORITY_CLASSES = {
  urgent: "bg-red-50 text-red-700 border-red-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-gray-50 text-gray-600 border-gray-200",
};

function timeAgo(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const TicketCard = ({ ticket, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="w-full text-left rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm hover:border-purple-300 hover:bg-purple-50/40 transition-colors"
  >
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-semibold text-gray-500">
        {ticket.account ? `${ticket.account} · ` : ""}#{ticket.number}
      </span>
      <span className="text-[11px] text-gray-400 shrink-0">
        {timeAgo(ticket.created_at)}
      </span>
    </div>
    <p className="mt-1 text-sm font-medium text-gray-900 line-clamp-2">
      {ticket.title}
    </p>
    {(ticket.priority || (ticket.tags || []).length > 0) && (
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {ticket.priority && (
          <span
            className={`inline-flex items-center rounded-full border px-1.5 py-px text-[10px] font-semibold uppercase ${
              PRIORITY_CLASSES[ticket.priority] || PRIORITY_CLASSES.low
            }`}
          >
            {ticket.priority}
          </span>
        )}
        {(ticket.tags || []).slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-px text-[10px] text-gray-600"
          >
            {tag}
          </span>
        ))}
      </div>
    )}
  </button>
);

/**
 * Pylon board buckets (New, Engineering) as clickable ticket cards. The
 * status tag on the ticket marks its bucket; refresh re-queries Pylon.
 */
const TicketBuckets = () => {
  const navigate = useNavigate();
  const [buckets, setBuckets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const loadBuckets = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const data = await getTicketBuckets();
    setIsLoading(false);
    if (data.error) {
      setLoadError(data.error);
      return;
    }
    setBuckets(Array.isArray(data.buckets) ? data.buckets : []);
  }, []);

  useEffect(() => {
    loadBuckets();
  }, [loadBuckets]);

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700">Ticket queues</p>
        <button
          type="button"
          onClick={loadBuckets}
          disabled={isLoading}
          className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-ktx-purple disabled:opacity-50"
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-3 w-3" aria-hidden />
          )}
          Refresh
        </button>
      </div>

      {loadError && (
        <p className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {loadError}
        </p>
      )}

      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
        {(buckets.length > 0
          ? buckets
          : [
              { key: "new", label: "New", tickets: [] },
              { key: "engineering", label: "Engineering", tickets: [] },
            ]
        ).map((bucket) => {
          const Icon = BUCKET_ICONS[bucket.key] || Inbox;
          return (
            <div
              key={bucket.key}
              className="rounded-lg border border-gray-200 bg-gray-50/60 overflow-hidden"
            >
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 bg-white">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  <Icon
                    className="inline h-3.5 w-3.5 mr-1.5 text-ktx-purple -translate-y-px"
                    aria-hidden
                  />
                  {bucket.label}
                </h4>
                <span className="text-[11px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                  {bucket.tickets?.length ?? 0}
                </span>
              </div>

              <div className="p-2 space-y-2 max-h-80 overflow-y-auto">
                {bucket.error && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
                    Could not load this queue: {bucket.error}
                  </p>
                )}
                {isLoading && !bucket.tickets?.length && !bucket.error && (
                  <p className="text-xs text-gray-400 px-1 py-2">Loading…</p>
                )}
                {!isLoading && !bucket.error && bucket.tickets?.length === 0 && (
                  <p className="text-xs text-gray-400 px-1 py-2">
                    No tickets in this queue.
                  </p>
                )}
                {(bucket.tickets || []).map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onOpen={() =>
                      navigate(supportTicketPath(ticket.number ?? ticket.id))
                    }
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TicketBuckets;
